import React, { useState, useEffect, useRef } from 'react';
import { X, Loader2, PauseCircle, Check } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { ExecutionModalTask } from './ExecutionModalTask';
import { ExecutionModalSpareParts } from './ExecutionModalSpareParts';
import { ExecutionModalAttachments } from './ExecutionModalAttachments';

export const ExecutionModalReschedule = ({ isOpen, onClose, onFinish, workOrder, setWorkOrder, onConfirmReschedule }) => {

    const [schedule_date, setScheduleDate] = useState(workOrder?.schedule_date || new Date().toISOString());
    const [isNewDate, setIsNewDate] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [localModalError, setLocalModalError] = useState(null);
    const [localIsSubmitting, setLocalIsSubmitting] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);

    const invalidateWorkOrders = () => {
        queryClient.invalidateQueries({ queryKey: ['wo'] });
    };

    const formRef = useRef(null);

    const dateFormat = (string) => {
        if (!string) return null;
        const date = string.split('T')[0];
        const clock = string.split('T')[1];
        const [year, month, day] = date.split('-');
        const [hour, minute] = clock.split(':');
        return `${day}/${month}/${year} ${hour}:${minute}`;
    };

    const validate = () => {
        if (new Date(schedule_date).getTime() <= new Date().getTime()) {
            setErrorMessage('Schedule date cannot be in the past');
            return false;
        }
        return true;
    }

    const handleReschedule = () => {
        if (!validate()) {
            return;
        }
        setLocalIsSubmitting(true);
        try {
            setIsNewDate(true);
            setShowConfirmationModal(true)

        } catch (error) {
            console.error(error);
        } finally {
            setLocalIsSubmitting(false);
        }
    }

    const handleConfirmReschedule = () => {
        setLocalIsSubmitting(true);
        try {
            if (isNewDate) {
                workOrder.scheduled_date = new Date(schedule_date).toISOString();
                setWorkOrder(workOrder);
            }
            onConfirmReschedule(workOrder);

        }
        catch (error) {
            console.error(error);
        } finally {
            setLocalIsSubmitting(false);
            onClose();
        }
    }
    // Reset form fields when modal opens
    useEffect(() => {
        if (isOpen) {
            setLocalModalError(null);
            setErrorMessage(null);
        }
    }, [isOpen]);


    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center gap-3 justify-center p-4 bg-black/60 backdrop-blur-sm border border-white">
            <div className={`bg-gray-800 rounded-2xl border border-gray-700 shadow-xl min-w-0 max-h-[90vh] overflow-hidden flex flex-col`}>
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
                    <h2 className="text-2xl font-semibold text-white">
                        {workOrder?.title || 'Work Order'} <br />
                    </h2>

                </div>
                {showConfirmationModal && (
                    <div className="flex flex-col">
                        <div className="bg-gray-700/50 rounded-xl px-4 py-2 mx-6 mt-6">
                            <p className="text-sm text-gray-400 mb-1">{`The next Work Order [ ${workOrder?.title || 'N/A'} ] will be scheduled for:`}</p>
                            <p className="text-white font-medium">{`${dateFormat(isNewDate ? new Date(schedule_date).toISOString() : workOrder?.scheduled_date)}`}</p>
                        </div>

                        <div className="px-6 py-4 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => { setShowConfirmationModal(false) }}
                                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-gray-300 text-sm font-medium hover:bg-gray-600 hover:text-white transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={localIsSubmitting}
                            >
                                I changed my mind
                            </button>
                            <button
                                type="button"
                                onClick={() => { handleConfirmReschedule() }}
                                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-green-600 bg-green-600 text-white text-sm font-medium hover:bg-green-700 hover:border-green-700 transition-colors cursor-pointer"
                                disabled={localIsSubmitting}
                            >
                                <p>Understood</p>
                            </button>

                        </div>


                    </div>
                )}
                {!showConfirmationModal && (
                    <div className="flex flex-col">
                        <div className="bg-gray-700/50 rounded-xl px-4 py-2 mx-6 mt-6">
                            <p className="text-sm text-gray-400 mb-1">{`The next Work Order [ ${workOrder?.title || 'N/A'} ] is scheduled for:`}</p>
                            <p className="text-white font-medium">{dateFormat(workOrder?.scheduled_date)}</p>
                        </div>
                        <div className="mx-6 mt-2">
                            <p className='text-white text-sm font-medium text-gray-300'>If you want, you can fill the field below to modify the next execution date.</p>
                        </div>
                        <div className="mx-6 mt-2">
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                New Scheduled Date
                            </label>
                            <input
                                type="datetime-local"
                                name="schedule_date"
                                value={schedule_date
                                    ? (() => {
                                        try {
                                            const date = new Date(schedule_date)
                                            if (isNaN(date.getTime())) {
                                                // If date invalid, use current date
                                                return new Date().toISOString().slice(0, 16)
                                            }
                                            return date.toISOString().slice(0, 16)
                                        } catch {
                                            // If there is an error parsing, use the current date
                                            return new Date().toISOString().slice(0, 16)
                                        }
                                    })()
                                    : new Date().toISOString().slice(0, 16) // Default: current date and time
                                }
                                onChange={(e) => {
                                    // Convert datetime-local value to ISO string
                                    const isoValue = e.target.value ? new Date(e.target.value).toISOString() : new Date().toISOString()
                                    setScheduleDate(isoValue)
                                    setErrorMessage('')
                                }}
                                className={`w-full px-4 py-2 rounded-lg border bg-gray-700 text-white ${errorMessage ? 'border-red-500' : 'border-gray-600'
                                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                disabled={localIsSubmitting}
                            />
                            {errorMessage !== '' && <p className="mt-1 text-sm text-red-400">{errorMessage}</p>}
                        </div>

                        <div className="px-6 py-4 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => { setIsNewDate(false), setShowConfirmationModal(true) }}
                                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-gray-300 text-sm font-medium hover:bg-gray-600 hover:text-white transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={localIsSubmitting}
                            >
                                Keep currently programmed date
                            </button>
                            <button
                                type="button"
                                onClick={() => { handleReschedule() }}
                                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-green-600 bg-green-600 text-white text-sm font-medium hover:bg-green-700 hover:border-green-700 transition-colors cursor-pointer"
                                disabled={localIsSubmitting}
                            >
                                <p>Submit new date</p>
                            </button>

                        </div>


                    </div>
                )}

            </div>

        </div>
    );
};
